import * as React from 'react';
import { BaSeTheme } from '../../theme';
import { ThemeNamedColorOrColorValue } from '../../theme/theme-interface';
import { BaSeParagraph } from '../typography/paragraph/paragraph';
import { BaSeSmall1 } from '../typography/small/small1';
import * as Styled from './slider-styled';

interface BaSeRangeSliderProps
  extends Pick<Styled.InputRangeProps, 'width' | 'orientation'>,
    Partial<Pick<Styled.InputRangeProps, 'height'>> {
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
  color?: ThemeNamedColorOrColorValue;
  label?: React.ReactNode;
  showTooltip?: boolean;
  showValue?: boolean;
  showMinMax?: boolean;
  minLabel?: React.ReactNode;
  maxLabel?: React.ReactNode;
  formatValue?(value: number): React.ReactNode;
  range?: {
    value: number;
    onChange(value: number): void;
  };
  value: number;
  onChange(value: number): void;
}

const {
  institucionais: { azulSebrae36, cinzaSebrae45 },
  feedbackInterface: { sucesso26, erro45, atencao55 },
  defaultColors: { white },
} = BaSeTheme.colors;

const colorMap = {
  default: azulSebrae36,
  confirmation: sucesso26,
  destructive: erro45,
  attention: atencao55,
} as const;

export const BaSeRangeSlider: React.FC<BaSeRangeSliderProps> = ({
  min = 0,
  max = 100,
  step = 1,
  isDisabled = false,
  width = '100%',
  height = '12.5em',
  orientation = 'horizontal',
  color: externalColor = 'default',
  label,
  showTooltip = false,
  showValue = false,
  showMinMax = false,
  minLabel,
  maxLabel,
  formatValue = (value) => value,
  range,
  value: externalValue,
  onChange,
}) => {
  const [activeThumb, setActiveThumb] = React.useState<'start' | 'end' | null>(
    null,
  );
  const isDraggingRef = React.useRef(false);

  if (step > max) {
    throw Error('[BaSeRangeSlider] "step" não pode ser maior que o "max"!');
  }
  if (min > max) {
    throw Error('[BaSeRangeSlider] "min" não pode ser maior que o "max"!');
  }
  if (max < min) {
    throw Error('[BaSeRangeSlider] "max" não pode ser menor que o "min"!');
  }
  if (max === min) {
    throw Error(
      '[BaSeRangeSlider] "max" e "min" devem ter os valores diferentes!',
    );
  }

  const value = React.useMemo(() => {
    if (externalValue <= min) {
      return min;
    }
    if (externalValue >= max) {
      return max;
    }
    return externalValue;
  }, [externalValue, max, min]);

  const color = React.useMemo(
    () => colorMap?.[externalColor as keyof typeof colorMap] ?? externalColor,
    [externalColor],
  );

  const percentage = React.useMemo(
    () => ((value - min) / (max - min)) * 100,
    [value, min, max],
  );

  const rangeValue = React.useMemo(() => {
    return range
      ? Math.max(value, Math.min(Math.max(range.value, min), max))
      : undefined;
  }, [range, value, min, max]);

  const rangePercentage = React.useMemo(() => {
    return rangeValue === undefined
      ? percentage
      : ((rangeValue - min) / (max - min)) * 100;
  }, [rangeValue, percentage, min, max]);

  const accessibleLabel = React.useMemo(() => {
    return typeof label === 'string' ? label : undefined;
  }, [label]);

  const hasMinLabel = React.useMemo(() => {
    return minLabel !== undefined && minLabel !== null;
  }, [minLabel]);

  const hasMaxLabel = React.useMemo(() => {
    return maxLabel !== undefined && maxLabel !== null;
  }, [maxLabel]);

  const shouldShowValues = React.useMemo(() => {
    return showValue || showTooltip;
  }, [showValue, showTooltip]);

  const handlePointerEnter = React.useCallback((thumb: 'start' | 'end') => {
    setActiveThumb(thumb);
  }, []);

  const handlePointerLeave = React.useCallback(() => {
    if (!isDraggingRef.current) {
      setActiveThumb(null);
    }
  }, []);

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLInputElement>, thumb: 'start' | 'end') => {
      isDraggingRef.current = true;
      setActiveThumb(thumb);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [],
  );

  const handlePointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLInputElement>) => {
      isDraggingRef.current = false;
      event.currentTarget.releasePointerCapture?.(event.pointerId);

      const { clientX, clientY } = event;
      const { left, right, top, bottom } =
        event.currentTarget.getBoundingClientRect();
      if (
        clientX < left ||
        clientX > right ||
        clientY < top ||
        clientY > bottom
      ) {
        setActiveThumb(null);
      }
    },
    [],
  );

  const handlePointerCancel = React.useCallback(() => {
    isDraggingRef.current = false;
    setActiveThumb(null);
  }, []);

  const limits = showMinMax && (hasMinLabel || hasMaxLabel) && (
    <Styled.Limits
      orientation={orientation}
      $alignment={
        hasMinLabel && hasMaxLabel ? 'both' : hasMinLabel ? 'start' : 'end'
      }
    >
      {hasMinLabel && <BaSeSmall1 color={cinzaSebrae45}>{minLabel}</BaSeSmall1>}
      {hasMaxLabel && <BaSeSmall1 color={cinzaSebrae45}>{maxLabel}</BaSeSmall1>}
    </Styled.Limits>
  );

  return (
    <Styled.SliderContainer
      width={width}
      height={height}
      orientation={orientation}
    >
      {label && (
        <Styled.SliderLabel>
          <BaSeParagraph color={cinzaSebrae45}>{label}</BaSeParagraph>
        </Styled.SliderLabel>
      )}

      <Styled.SliderControl height={height} orientation={orientation}>
        <Styled.SliderTrack
          color={color}
          orientation={orientation}
          $startPercentage={range ? percentage : 0}
          $percentage={rangePercentage}
          $isRange={!!range}
          $isDisabled={isDisabled}
        />

        {orientation === 'vertical' && limits}

        {shouldShowValues &&
          rangeValue !== undefined &&
          (!showTooltip || activeThumb !== 'start') && (
            <Styled.ValueLabel
              $percentage={percentage}
              orientation={orientation}
            >
              <BaSeSmall1 color={cinzaSebrae45} isBold={true}>
                {formatValue(value)}
              </BaSeSmall1>
            </Styled.ValueLabel>
          )}

        {shouldShowValues &&
          (!showTooltip ||
            activeThumb !== (rangeValue === undefined ? 'start' : 'end')) && (
            <Styled.ValueLabel
              $percentage={rangePercentage}
              orientation={orientation}
            >
              <BaSeSmall1 color={cinzaSebrae45} isBold={true}>
                {formatValue(rangeValue ?? value)}
              </BaSeSmall1>
            </Styled.ValueLabel>
          )}

        {showTooltip && activeThumb && (
          <Styled.ValueTooltip
            $percentage={activeThumb === 'start' ? percentage : rangePercentage}
            orientation={orientation}
          >
            <BaSeSmall1 color={white}>
              {formatValue(
                activeThumb === 'start' ? value : rangeValue ?? value,
              )}
            </BaSeSmall1>
          </Styled.ValueTooltip>
        )}

        <Styled.InputRange
          $percentage={rangePercentage}
          $startPercentage={range ? percentage : 0}
          $isRange={!!range}
          $hasExternalTrack
          width={width}
          height={height}
          orientation={orientation}
          color={color}
          value={value}
          disabled={isDisabled}
          min={min}
          max={max}
          step={step}
          aria-label={
            range
              ? `${accessibleLabel ?? 'Intervalo'} inicial`
              : accessibleLabel
          }
          onPointerEnter={() => handlePointerEnter('start')}
          onPointerLeave={handlePointerLeave}
          onPointerDown={(event) => handlePointerDown(event, 'start')}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onFocus={() => setActiveThumb('start')}
          onBlur={() => setActiveThumb(null)}
          onInput={(e) =>
            onChange(
              rangeValue === undefined
                ? +e.currentTarget.value
                : Math.min(+e.currentTarget.value, rangeValue),
            )
          }
        />

        {rangeValue !== undefined && (
          <Styled.InputRange
            $percentage={rangePercentage}
            $startPercentage={percentage}
            $isRange
            $hasExternalTrack
            width={width}
            height={height}
            orientation={orientation}
            color={color}
            value={rangeValue}
            disabled={isDisabled}
            min={min}
            max={max}
            step={step}
            aria-label={`${accessibleLabel ?? 'Intervalo'} final`}
            onPointerEnter={() => handlePointerEnter('end')}
            onPointerLeave={handlePointerLeave}
            onPointerDown={(event) => handlePointerDown(event, 'end')}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onFocus={() => setActiveThumb('end')}
            onBlur={() => setActiveThumb(null)}
            onInput={(e) =>
              range?.onChange(Math.max(+e.currentTarget.value, value))
            }
          />
        )}
      </Styled.SliderControl>

      {orientation === 'horizontal' && limits}
    </Styled.SliderContainer>
  );
};

BaSeRangeSlider.displayName = 'BaSeRangeSlider';
